import { CircleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { MessageNodeHeader } from '@/modules/tryIt/components/Messages/MessageNode/MessageNodeHeader';
import { NodeType } from '@/shared/types/tryIt';
import { formatDate } from '@/util/dateUtils';

import { selectIsReplay } from '../../redux/selectors';
import { hasTurnError } from '../../shared/turnError';
import { TurnSourceLabel, useTurnSource } from '../../shared/TurnSource';
import { ConversationTurn } from '../../types/conversation';

type Props = {
  turn: ConversationTurn;
  index: number;
  /** Legacy node, for the step icons the header shows on hover or when selected. */
  node?: NodeType;
  brainId?: string;
  isActive?: boolean;
};

/** Timeline turn header: `Turn n · <source>` with a rule running to the right edge. */
export const TurnRowHeader = ({ turn, index, node, brainId, isActive }: Props) => {
  const { t } = useTranslation();
  const isReplay = useSelector(selectIsReplay);

  const source = useTurnSource(turn);
  const hasError = hasTurnError(turn);
  const isPending = Boolean(turn.pending);

  if (turn.category.kind === 'agent') {
    return null;
  }

  return (
    <div className="flex items-center gap-4 p-4">
      <div className="flex shrink-0 items-center gap-1.5">
        <span className="text-xs leading-4 font-medium text-foreground">
          {t('try_it.turn_number', { 0: index + 1 })}
        </span>
        {hasError && (
          <>
            <span className="text-xs leading-4 text-foreground-muted">·</span>
            <span className="flex items-center gap-1 text-xs leading-4 font-medium text-foreground-muted">
              <CircleAlertIcon size={14} className="shrink-0" />
              {t('common.error')}
            </span>
          </>
        )}
        {!hasError && isPending && (
          <>
            <span className="text-xs leading-4 text-foreground-muted">·</span>
            <Skeleton className="h-3 w-24 border border-stroke" />
          </>
        )}
        {!hasError && !isPending && source && (
          <>
            <span className="text-xs leading-4 text-foreground-muted">·</span>
            <TurnSourceLabel source={source} variant="row" />
          </>
        )}
      </div>
      <Separator className="flex flex-1 flex-col items-start" />
      {isReplay && node?.time && (
        <span
          className={cn(
            'shrink-0 text-xs leading-4 font-normal text-foreground-muted',
            !isActive && 'hidden group-hover:block'
          )}
        >
          {`${formatDate(node.time, 'MMM d, yyyy')} · ${formatDate(node.time, 'hh:mm a')}`}
        </span>
      )}
      {!isReplay && node && brainId && (
        <MessageNodeHeader
          iconsOnly
          isActive={isActive}
          id={node.id}
          brainId={brainId}
          collection={node.collection}
          index={index}
          author_type={node.author_type}
          steps={node.steps}
          messages={node.messages}
          name={node.name}
          nodes_stack={node.nodes_stack}
          broadcastId={node.broadcast_id}
          campaignId={node.campaign_id}
        />
      )}
    </div>
  );
};
